<script setup lang="ts">
import { GAME_TYPE } from "~/constants";
import useEventCommerce from "~/hooks/events/use-event-commerce";
import formatDate from "~/utils/format-date";

export interface CardUpcomingProps {
  title?: string;
  gameStartDate?: null | number | string | Date;
  sportType?: TSportType;
  commerceCoverage: TCommerceCategory[];
  team1Name?: string;
  team2Name?: string;
  team1Color?: string;
  team2Color?: string;
  team1LogoUrl?: string;
  team2LogoUrl?: string;
  isEventAvailableForUser?: boolean;
  gameType: TGameType;
}

const props = defineProps<CardUpcomingProps>();

const { locale: currentLocale } = useI18n();

const eventDate = computed(() => formatDate(
  props.gameStartDate,
  currentLocale.value === "ko" ? "MMMM do" : "d MMMM",
  currentLocale.value as TAvailableLocale,
));
const dateHours = formatDate(props.gameStartDate, "HH:mm", currentLocale.value as TAvailableLocale);
const isPracticeGameType = computed(() => props.gameType === GAME_TYPE.TRAINING || Boolean(props.team1Name && !props.team2Name));
const isOtherGameType = computed(() => props.gameType === GAME_TYPE.OTHER || Boolean(!props.team1Name && !props.team2Name));
const { commerceType } = useEventCommerce(props.commerceCoverage);
</script>

<template>
  <div v-motion-pop class="cardUpcoming">
    <div class="cardUpcoming__date text-main pt-8 pb-4 px-1">
      <span class="text-xs">{{ eventDate }}</span>
      <span class="text-lg font-bold mb-5">{{ dateHours }}</span>
    </div>

    <div class="cardUpcoming__content">
      <div v-if="!props.isEventAvailableForUser" class="cardUpcoming__lock text-caption-medium">
        <IconLock class="z-10" />

        <span class="z-10">{{ commerceType }}</span>
      </div>
      <div class="flex-1">
        <BlockTeamsInfo
          class="h-14 mt-1"
          :team1-name="props.team1Name"
          :team2-name="props.team2Name"
          :team1-color="props.team1Color"
          :team2-color="props.team2Color"
          :team1-logo-url="props.team1LogoUrl"
          :team2-logo-url="props.team2LogoUrl"
          :title="props.title"
          :game-type="props.gameType"
        />
        <EventMetaInfo
          :title="props.title"
          :team-name="props.team1Name"
          :is-other-game-type="isOtherGameType"
          :is-practice-game-type="isPracticeGameType"
          :sport-type="props.sportType"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.cardUpcoming {
  @apply flex flex-row max-w-full
  overflow-hidden cursor-pointer
  transition duration-100;

  &:hover {
    box-shadow: var(--base-box-shadow);
  }

  background-color: var(--base-card-bgColor);
  border-radius: var(--base-border-radius);

  .cardUpcoming__date {
    @apply w-20 flex flex-col flex-shrink-0 justify-center items-center text-center;

    background-color: var(--base-card-bgSecondaryColor);
  }

  .cardUpcoming__content {
    @apply relative p-3 pt-6
    max-w-full flex-grow-0
    flex flex-col justify-between;

    width: calc(100% - 64px);
  }

  .cardUpcoming__lock {
    @apply absolute top-0 right-0 px-2 py-1 overflow-hidden flex justify-center items-center gap-[0.5rem]
    rounded-tr rounded-bl rtl:right-auto rtl:left-0 rtl:rounded-tr-none rtl:rounded-bl-none rtl:rounded-br;

    color: var(--base-secondary-color);

    &::before {
      @apply absolute w-full h-full z-0;

      content: '';
      background-color: var(--base-card-bgSecondaryColor);
    }
  }
}
</style>
